import { useState, useCallback, useMemo } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import {
  Container,
  Title,
  Table,
  TextInput,
  Select,
  Group,
  ActionIcon,
  Text,
  Paper,
  Stack,
  Skeleton,
  Badge,
  Code,
  Modal,
  ScrollArea,
  Pagination,
  Menu,
} from '@mantine/core';
import { DatePickerInput, type DatesRangeValue } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import {
  IconSearch,
  IconDotsVertical,
  IconEye,
  IconCalendar,
} from '@tabler/icons-react';
import { adminActionsService } from '../../services/api/admin-actions';
import { useTableSort } from '../../hooks';
import { SortableHeader } from '../../components/shared';
import { ACTION_TYPE_COLORS, ACTION_TYPE_LABELS } from '../../constants';
import type { AdminActionWithUser, AdminActionType, AdminActionEntityType } from '../../types';
import dayjs from 'dayjs';

export function AuditLogPage() {
  const [actionTypeFilter, setActionTypeFilter] = useState<string | null>(null);
  const [entityTypeFilter, setEntityTypeFilter] = useState<string | null>(null);
  const [entityIdSearch, setEntityIdSearch] = useState('');
  const [debouncedEntityIdSearch] = useDebouncedValue(entityIdSearch, 300);
  const [dateRange, setDateRange] = useState<DatesRangeValue>([null, null]);
  const [page, setPage] = useState(1);
  const [detailsModalOpened, { open: openDetailsModal, close: closeDetailsModal }] =
    useDisclosure(false);
  const [selectedAction, setSelectedAction] = useState<AdminActionWithUser | null>(null);

  const { data: actionsData, isLoading } = useQuery({
    queryKey: ['admin-actions', actionTypeFilter, entityTypeFilter, debouncedEntityIdSearch, dateRange],
    queryFn: () =>
      adminActionsService.list({
        actionType: (actionTypeFilter as AdminActionType) || undefined,
        entityType: (entityTypeFilter as AdminActionEntityType) || undefined,
        entityId: debouncedEntityIdSearch || undefined,
        fromDate: dateRange[0] ? dayjs(dateRange[0]).startOf('day').toISOString() : undefined,
        toDate: dateRange[1] ? dayjs(dateRange[1]).endOf('day').toISOString() : undefined,
        limit: 100,
      }),
  });

  const viewDetails = (action: AdminActionWithUser) => {
    setSelectedAction(action);
    openDetailsModal();
  };

  const resetPage = useCallback(() => setPage(1), []);
  const { sortColumn, sortDirection, handleSort, sortData } = useTableSort<AdminActionWithUser>(resetPage);

  const actions = actionsData?.actions || [];

  const sortAccessors: Record<string, (item: AdminActionWithUser) => unknown> = useMemo(() => ({
    timestamp: (a) => a.createdAt,
    action: (a) => a.actionType,
    entityType: (a) => a.entityType,
    entityId: (a) => a.entityId,
    admin: (a) => a.adminUserEmail,
    reason: (a) => a.reason,
  }), []);

  const sortedActions = sortData(actions, sortAccessors);

  const PAGE_SIZE = 20;
  const totalPages = Math.ceil(sortedActions.length / PAGE_SIZE);
  const paginatedActions = sortedActions.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="lg">
        <Title order={2}>Audit Log</Title>
      </Group>

      <Paper withBorder p="md" mb="md">
        <Stack gap="sm">
          <Group>
            <TextInput
              placeholder="Search by Entity ID..."
              leftSection={<IconSearch size={16} />}
              value={entityIdSearch}
              onChange={e => { setEntityIdSearch(e.target.value); setPage(1); }}
              style={{ flex: 1 }}
            />
          </Group>
          <Group>
            <DatePickerInput
              type="range"
              placeholder="Date range"
              leftSection={<IconCalendar size={16} />}
              value={dateRange}
              onChange={(v) => { setDateRange(v); setPage(1); }}
              clearable
              style={{ flex: 1 }}
            />
            <Select
              placeholder="Action Type"
              clearable
              value={actionTypeFilter}
              onChange={(v) => { setActionTypeFilter(v); setPage(1); }}
              data={[
                { value: 'create', label: 'Create' },
                { value: 'update', label: 'Update' },
                { value: 'delete', label: 'Delete' },
                { value: 'status_change', label: 'Status Change' },
                { value: 'revoke', label: 'Revoke' },
                { value: 'suspend', label: 'Suspend' },
                { value: 'reactivate', label: 'Reactivate' },
                { value: 'transfer', label: 'Transfer' },
                { value: 'manual_deactivate', label: 'Manual Deactivate' },
                { value: 'login', label: 'Login' },
                { value: 'logout', label: 'Logout' },
                { value: 'activate', label: 'Activate (Device)' },
                { value: 'validate', label: 'Validate (Device)' },
                { value: 'deactivate', label: 'Deactivate (Device)' },
                { value: 'reactivate_device', label: 'Reactivate (Device)' },
              ]}
              w={180}
            />
            <Select
              placeholder="Entity Type"
              clearable
              value={entityTypeFilter}
              onChange={(v) => { setEntityTypeFilter(v); setPage(1); }}
              data={[
                { value: 'license', label: 'License' },
                { value: 'activation', label: 'Activation' },
                { value: 'customer', label: 'Customer' },
                { value: 'product', label: 'Product' },
                { value: 'user', label: 'User' },
              ]}
              w={150}
            />
          </Group>
        </Stack>
      </Paper>

      <Paper withBorder>
        {isLoading ? (
          <Stack p="md" gap="xs">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} height={50} />
            ))}
          </Stack>
        ) : actions.length === 0 ? (
          <Text p="xl" ta="center" c="dimmed">
            No audit log entries found
          </Text>
        ) : (
          <>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <SortableHeader column="timestamp" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Timestamp</SortableHeader>
                <SortableHeader column="action" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Action</SortableHeader>
                <SortableHeader column="entityType" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Entity Type</SortableHeader>
                <SortableHeader column="entityId" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Entity ID</SortableHeader>
                <SortableHeader column="admin" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Admin</SortableHeader>
                <SortableHeader column="reason" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Reason</SortableHeader>
                <Table.Th w={50}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {paginatedActions.map(action => (
                <Table.Tr key={action.id} onClick={() => viewDetails(action)} style={{ cursor: 'pointer' }}>
                  <Table.Td>
                    <Text size="sm">
                      {dayjs(action.createdAt).format('MMM D, YYYY h:mm:ss A')}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={ACTION_TYPE_COLORS[action.actionType]} variant="light">
                      {ACTION_TYPE_LABELS[action.actionType]}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color="gray">
                      {action.entityType}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Code>{action.entityId.substring(0, 8)}...</Code>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{action.adminUserEmail}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed" lineClamp={1}>
                      {action.reason || '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Menu position="bottom-end" withArrow>
                      <Menu.Target>
                        <ActionIcon variant="subtle" onClick={(e) => e.stopPropagation()}>
                          <IconDotsVertical size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={<IconEye size={14} />}
                          onClick={() => viewDetails(action)}
                        >
                          View
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Group justify="space-between" px="md" py="sm" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
            <Text size="sm" c="dimmed">
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, sortedActions.length)}–{Math.min(page * PAGE_SIZE, sortedActions.length)} of {sortedActions.length}
            </Text>
            {totalPages > 1 && (
              <Pagination value={page} onChange={setPage} total={totalPages} size="sm" />
            )}
          </Group>
          </>
        )}
      </Paper>

      {/* Details Modal */}
      <Modal
        opened={detailsModalOpened}
        onClose={closeDetailsModal}
        title="Audit Log Details"
        size="lg"
      >
        {selectedAction && (
          <Stack gap="md">
            <Group>
              <Text size="sm" fw={500} w={120}>
                Timestamp:
              </Text>
              <Text size="sm">
                {dayjs(selectedAction.createdAt).format('MMMM D, YYYY h:mm:ss A')}
              </Text>
            </Group>
            <Group>
              <Text size="sm" fw={500} w={120}>
                Action:
              </Text>
              <Badge color={ACTION_TYPE_COLORS[selectedAction.actionType]} variant="light">
                {ACTION_TYPE_LABELS[selectedAction.actionType]}
              </Badge>
            </Group>
            <Group>
              <Text size="sm" fw={500} w={120}>
                Entity Type:
              </Text>
              <Badge variant="light" color="gray">
                {selectedAction.entityType}
              </Badge>
            </Group>
            <Group>
              <Text size="sm" fw={500} w={120}>
                Entity ID:
              </Text>
              <Code>{selectedAction.entityId}</Code>
            </Group>
            <Group>
              <Text size="sm" fw={500} w={120}>
                Admin:
              </Text>
              <Text size="sm">{selectedAction.adminUserEmail}</Text>
            </Group>
            {selectedAction.reason && (
              <Group align="flex-start">
                <Text size="sm" fw={500} w={120}>
                  Reason:
                </Text>
                <Text size="sm" style={{ flex: 1 }}>
                  {selectedAction.reason}
                </Text>
              </Group>
            )}
            {selectedAction.oldValues && Object.keys(selectedAction.oldValues).length > 0 && (
              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  Previous Values:
                </Text>
                <ScrollArea mah={150}>
                  <Code block>{JSON.stringify(selectedAction.oldValues, null, 2)}</Code>
                </ScrollArea>
              </Stack>
            )}
            {selectedAction.newValues && Object.keys(selectedAction.newValues).length > 0 && (
              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  New Values:
                </Text>
                <ScrollArea mah={150}>
                  <Code block>{JSON.stringify(selectedAction.newValues, null, 2)}</Code>
                </ScrollArea>
              </Stack>
            )}
          </Stack>
        )}
      </Modal>
    </Container>
  );
}
